import { isIndividualContributorContent } from '../util';
import areAdsEnabledWithReasons from './areAdsEnabledWithReasons';
import envconfig from './config';
// Given an analytics model (e.g. returned from getModelForItem) and a mapping
// from model keys to GA custom dimension indices, return a mapping from GA
// custom dimension indices to the corresponding values.
export const mapGoogleAnalyticsDimensions = (model, mapping) => Object.keys(mapping ?? {}).reduce((acc, key) => {
    const dimensionIdx = mapping?.[key];
    if (dimensionIdx == null || dimensionIdx === '') {
        return acc;
    }
    const value = model[key];
    if (value != null && value !== '') {
        acc[dimensionIdx] = `${value}`;
    }
    return acc;
}, {});
/**
 * Generate the primary domain for a site for the purposes of Google Analytics
 * reporting. This is the canonical hostname in prod (e.g. www.biography.com)
 * or with a "dev." prefix in QA/dev (e.g. dev.www.biography.com)
 *
 * @param {site} site to generate primary domain for
 * @returns the primary domain
 */
export const sitePrimaryDomain = (site, env) => env === 'PROD' ? site.config.siteProductionDomain : `dev.${site.config.siteProductionDomain}`;
const PAGETYPE_DATA_EXTENSIONS = ['si_team', 'si_game', 'ticker'];
export const makeGoogleAnalyticsConfig = ({ item, path, site, channelName, featureFlagChecker = {}, subpage, }) => {
    const trackers = [];
    const gaConfig = site.config.analytics.providers.GoogleAnalytics;
    const cookieDomain = process.env.NODE_ENV === 'production' ? gaConfig.domain : 'none';
    const siteSampleRate = gaConfig.sampleRate;
    const platformSampleRate = gaConfig.tempestSampleRate;
    const platformTracker = {
        name: '_tempestTracker',
        id: envconfig.googleAnalytics.tempestGA4Tracker,
        sampleRate: platformSampleRate,
        cookieDomain,
        sendEvents: true,
        setFieldValues: true,
        customDimensionMap: envconfig.customDimensionMap,
        customEventDimensionMap: envconfig.customEventDimensionMap,
    };
    trackers.push(platformTracker);
    if (channelName === 'SmartNews' || channelName === 'Newsbreak') {
        trackers.push({
            name: '_defaultTracker',
            id: envconfig.googleAnalytics.smartnewsGA4Tracker,
            sampleRate: platformSampleRate,
            cookieDomain: cookieDomain,
            sendEvents: true,
            setFieldValues: true,
            customDimensionMap: {
                premiumDetail: 9,
                channelName: 11,
                subscriptionStatus: 12,
                primaryDomain: 21,
                mavenChannel: 22,
                coreVertical: 23,
                specialtyVerticals: 24,
                monetization: 36,
                contentItemId: 38,
                contributorContent: 42,
            },
            customEventDimensionMap: {},
        });
    }
    (gaConfig.trackers ?? []).forEach((t, idx) => {
        const siteTrackerId = t.trackingId;
        const commonTrackerFields = {
            sampleRate: siteSampleRate,
            cookieDomain: cookieDomain,
            sendEvents: true,
            setFieldValues: true,
            contentItemIdPrefix: t.contentItemIdPrefix,
            customDimensionMap: t.customDimensionMap,
        };
        if (siteTrackerId !== undefined && siteTrackerId) {
            trackers.push({
                ...commonTrackerFields,
                name: `_siteTracker${idx}`,
                id: siteTrackerId,
            });
        }
        if (t.GA4id !== undefined && t.GA4id) {
            trackers.push({
                ...commonTrackerFields,
                name: `_publisherPersonalGA4Tracker`,
                id: t.GA4id,
            });
        }
    });
    if (item.authorProfile?.googleAnalyticsTrackingId != null &&
        item.authorProfile.googleAnalyticsTrackingId) {
        trackers.push({
            name: '_hubpagesAuthorTracker',
            id: item.authorProfile.googleAnalyticsTrackingId,
            sampleRate: platformSampleRate,
            cookieDomain: cookieDomain,
            sendEvents: true,
            setFieldValues: true,
        });
    }
    // We're going to modify the model, so we'll make a copy of it
    // and then our changes won't affect the original object that might be
    // used by other parts of the system.
    let model = {};
    if (item.viewProperties?.analyticsModel != null) {
        model = JSON.parse(JSON.stringify(item.viewProperties.analyticsModel));
    }
    model.path = path;
    if (subpage !== undefined && path !== undefined) {
        model.path = path + (path.endsWith('/') ? '' : '/') + subpage;
    }
    if (PAGETYPE_DATA_EXTENSIONS.includes(item.extension ?? '')) {
        model.mavenPageType = 'data';
    }
    // Commerce Pages
    if ((item.objectType === 'ContentBasicPage' && item.extension?.endsWith('_subscribe')) ?? false) {
        const plans = item.offer?.extendedProperties?.subscription_plans ?? [];
        model.pageType = 'Commerce';
        model.mavenPageType = 'ecom.digital.offer';
        model.membershipProductName = item?.offer?.extendedProperties?.offer_title;
        model.membershipProductCode = item.extension;
        model.membershipOfferSlug = item.path;
        if (plans.length) {
            model.membershipPlanCodes = plans.map((plan) => plan.planCode).join('|');
        }
    }
    // PXP-1884 Organization pages
    // We don't want to pass author data for this page type.
    if (item.objectType === 'ContentOrganization') {
        delete model.authorName;
        delete model.author;
    }
    model.contributorContent = isIndividualContributorContent(site.config, item) ? 1 : 0;
    const ads = areAdsEnabledWithReasons(site.config, item, item.viewProperties);
    model.monetization = ads['enabled'] ? '1' : ads['reasons'].join(';');
    if (channelName) {
        model.channelName = channelName;
    }
    // Vertical information is currently purely based on site, rather than
    // section or content keywords.
    if (site.config.coreVertical != null && site.config.coreVertical) {
        model.coreVertical = site.config.coreVertical;
    }
    if (Array.isArray(site.config.specialtyVerticals) && site.config.specialtyVerticals.length > 0) {
        // For ease of searching, we want to wrap our specialty vertical
        // keywords, such as "luxury" or "travel", in square brackets.
        const verticals = site.config.specialtyVerticals.map((v) => `[${'' + v}]`).join(',');
        model.specialtyVerticals = verticals;
    }
    const env = envconfig.templateEnvironment.toUpperCase();
    const primaryDomain = sitePrimaryDomain(site, env);
    model.primaryDomain = primaryDomain;
    // Channel here refers to the market facing channel not Web, AMP, etc.
    // For Salish sites there are often many channels on one domain but
    // for Tempest there is one channel per domain so use the same value
    model.mavenChannel = site.config.channelKey;
    if (env !== 'PROD') {
        model.mavenChannel = `dev.${model.mavenChannel}`;
    }
    if (channelName === 'AMP') {
        model.demonetizedReason = '0';
    }
    model.contentItemId = item.id;
    model.siteType = site.config.siteType;
    model.premiumDetail = 0;
    if (item?.viewProperties?.components != null && Array.isArray(item?.viewProperties?.components)) {
        model.premiumDetail = item.viewProperties.components.some((componentConfig) => componentConfig.type === 'phxDetailPremium')
            ? 1
            : 0;
    }
    // Here we process down the list of features we care about. Any special
    // casing based on e.g. sessionStorage compared to the enabled value has to
    // be handled in the GA Pug template, since it requires access to the
    // client side.
    const features = site.config?.features2?.flags ?? {};
    model.features = Object.keys(features)
        .filter((featureName) => (features[featureName].track ?? false) && features[featureName].enabled > 0)
        .map((featureName) => `${featureName}:1`)
        .join(';');
    let type = 'ga4-gtag';
    if (featureFlagChecker?.isEnabled?.('use-ga4') ?? false) {
        type = 'ga4';
    }
    else if ((featureFlagChecker?.isEnabled?.('use-ga-ga4') ?? false) ||
        (featureFlagChecker?.isEnabled?.('use-ga') ?? false)) {
        type = 'ga-ga4';
    }
    else if (featureFlagChecker?.isEnabled?.('use-ga4-gtag') ?? false) {
        type = 'ga4-gtag';
    }
    return {
        initialModel: model,
        trackers,
        type,
        usePublisherGtag: featureFlagChecker?.isEnabled?.('use-publisher-gtag') ?? false,
        ids: trackers.map((t) => t.id),
    };
};
export const makeGoogleAnalyticsTrackersInfo = (site, item, channelName) => {
    const gaConfig = makeGoogleAnalyticsConfig({ item, site, channelName });
    const analyticsModel = gaConfig.initialModel;
    return gaConfig.trackers.map((tracker) => {
        const customDims = mapGoogleAnalyticsDimensions(analyticsModel, tracker.customDimensionMap);
        const dimensions = Object.keys(customDims).map((dimId) => ({
            key: `dimension${dimId}`,
            value: customDims[dimId],
        }));
        return {
            id: tracker.id,
            name: tracker.name,
            dimensions: dimensions,
        };
    });
};
export default {
    mapGoogleAnalyticsDimensions,
    makeGoogleAnalyticsConfig,
    makeGoogleAnalyticsTrackersInfo,
    sitePrimaryDomain,
};
//# sourceMappingURL=index.js.map